import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CUSTOMER_KEY, TOKEN_KEY, readToken } from './api';

/**
 * Who is logged in on the website. The same phone + OTP login as the app;
 * the token and profile live in localStorage so a reload keeps the session.
 *
 * `requireLogin(then)` opens the login sheet and runs `then` once the
 * customer is signed in — used by wishlist hearts and Book buttons.
 */
const CustomerAuthContext = createContext(null);

const readCustomer = () => {
  try {
    const raw = window.localStorage.getItem(CUSTOMER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

export const CustomerAuthProvider = ({ children }) => {
  const [token, setToken] = useState(readToken);
  const [customer, setCustomer] = useState(readCustomer);
  // null = closed; otherwise { then } to run after a successful login
  const [loginRequest, setLoginRequest] = useState(null);

  const login = useCallback((nextToken, nextCustomer) => {
    try {
      window.localStorage.setItem(TOKEN_KEY, nextToken);
      window.localStorage.setItem(CUSTOMER_KEY, JSON.stringify(nextCustomer || {}));
    } catch (error) {
      // Session just will not survive a reload
    }
    setToken(nextToken);
    setCustomer(nextCustomer || {});
  }, []);

  const updateCustomer = useCallback((patch) => {
    setCustomer((current) => {
      const next = { ...(current || {}), ...(patch || {}) };
      try {
        window.localStorage.setItem(CUSTOMER_KEY, JSON.stringify(next));
      } catch (error) {
        // ignore
      }
      return next;
    });
  }, []);

  const logout = useCallback(() => {
    try {
      window.localStorage.removeItem(TOKEN_KEY);
      window.localStorage.removeItem(CUSTOMER_KEY);
    } catch (error) {
      // ignore
    }
    setToken('');
    setCustomer(null);
  }, []);

  const requireLogin = useCallback(
    (then) => {
      if (readToken()) {
        if (then) then();
        return true;
      }
      setLoginRequest({ then: then || null });
      return false;
    },
    []
  );

  const closeLogin = useCallback(() => setLoginRequest(null), []);

  // Another tab logging out logs this one out too
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === TOKEN_KEY) {
        setToken(readToken());
        setCustomer(readCustomer());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo(
    () => ({
      token,
      customer,
      isLoggedIn: Boolean(token),
      login,
      logout,
      updateCustomer,
      requireLogin,
      loginRequest,
      closeLogin,
    }),
    [token, customer, login, logout, updateCustomer, requireLogin, loginRequest, closeLogin]
  );

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
};

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used inside CustomerAuthProvider');
  }
  return context;
};

export default CustomerAuthContext;
